import { useState } from "react"
import { Beaker, FileText, Lock, UserCheck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { EmptyState } from "@/components/empty-state"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AddQCLogDialog } from "@/features/lots/components/add-qc-log-dialog"
import { QCLogRow } from "@/features/lots/components/qc-log-row"
import type { LotDetail, QCKind } from "@/features/lots/api/types"
import { cn } from "@/lib/utils"

interface QCSectionProps {
  lot: LotDetail
}

export function QCSection({ lot }: QCSectionProps) {
  const { t } = useTranslation("lots")
  const [tab, setTab] = useState<QCKind>("CLIENT")
  const [addOpen, setAddOpen] = useState(false)

  const canAdd = lot.status === "COMPLETED"
  const locked = !canAdd
  const logs = lot.qcLogs ?? []
  const labCount = logs.filter((l) => l.kind === "LAB").length
  const clientCount = logs.filter((l) => l.kind === "CLIENT").length
  const otherCount = logs.filter((l) => l.kind === "OTHER").length
  const filtered = logs.filter((l) => l.kind === tab)

  if (locked) {
    return <LockedQCCard />
  }

  // After a successful create, switch the QC section to the tab that
  // corresponds to the kind the user just created and surface a toast
  // pointing at it. Without this the dialog closes and the new entry is
  // silently filed under whichever tab the user was on, which is the
  // confusion the operator reported.
  const handleCreated = (createdKind: QCKind) => {
    setTab(createdKind)
    const tabName =
      createdKind === "LAB"
        ? t("detail.qc.tabLab")
        : createdKind === "CLIENT"
          ? t("detail.qc.tabClient")
          : t("detail.qc.tabOther")
    toast.success(t("detail.qc.savedToast", { tab: tabName }))
  }

  const addLabel =
    tab === "LAB"
      ? t("detail.qc.addLab")
      : tab === "CLIENT"
        ? t("detail.qc.addClient")
        : t("detail.qc.addOther")

  const AddIcon = tab === "LAB" ? Beaker : tab === "CLIENT" ? UserCheck : FileText

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">
            {t("detail.qc.eyebrow")}
          </p>
          <h2 className="text-lg font-semibold tracking-tight">
            {t("detail.qc.title")}
          </h2>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <AddIcon className="size-4" />
          {addLabel}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as QCKind)}>
        <TabsList variant="line" className="justify-start gap-6">
          <TabsTrigger value="CLIENT">
            <UserCheck className="size-3.5" />
            {t("detail.qc.tabClient")}
            <span className="text-muted-foreground ml-1 tabular-nums">
              ({clientCount})
            </span>
          </TabsTrigger>
          <TabsTrigger value="LAB">
            <Beaker className="size-3.5" />
            {t("detail.qc.tabLab")}
            <span className="text-muted-foreground ml-1 tabular-nums">
              ({labCount})
            </span>
          </TabsTrigger>
          <TabsTrigger value="OTHER">
            <FileText className="size-3.5" />
            {t("detail.qc.tabOther")}
            <span className="text-muted-foreground ml-1 tabular-nums">
              ({otherCount})
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="pt-4">
          {filtered.length === 0 ? (
            <EmptyState
              title={
                tab === "LAB"
                  ? t("detail.qc.emptyLab")
                  : tab === "CLIENT"
                    ? t("detail.qc.emptyClient")
                    : t("detail.qc.emptyOther")
              }
            />
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map((log) => (
                <QCLogRow key={log.id} lot={lot} log={log} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AddQCLogDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        lotId={lot.id}
        defaultKind={tab}
        onCreated={handleCreated}
      />
    </Card>
  )
}

// Locked state: clearly different from the "completed but empty" state.
// Muted background, lock icon, dimmed disabled tab triggers, single
// explanatory copy block — no add button at all.
function LockedQCCard() {
  const { t } = useTranslation("lots")

  return (
    <Card className={cn("relative overflow-hidden border-dashed p-5")}>
      <div className="mb-3 flex items-start justify-between gap-3 opacity-70">
        <div>
          <p className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">
            {t("detail.qc.eyebrow")}
          </p>
          <h2 className="text-muted-foreground text-lg font-semibold tracking-tight">
            {t("detail.qc.title")}
          </h2>
        </div>
        <span className="text-muted-foreground inline-flex items-center gap-1.5 rounded-md border border-dashed px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide">
          <Lock className="size-3.5" />
          {t("detail.qc.lockedBadge")}
        </span>
      </div>

      <div className="opacity-60">
        <div className="border-border/60 flex items-center gap-6 border-b pb-2">
          <DisabledTab icon={<UserCheck className="size-3.5" />}>
            {t("detail.qc.tabClient")}
          </DisabledTab>
          <DisabledTab icon={<Beaker className="size-3.5" />}>
            {t("detail.qc.tabLab")}
          </DisabledTab>
          <DisabledTab icon={<FileText className="size-3.5" />}>
            {t("detail.qc.tabOther")}
          </DisabledTab>
        </div>
      </div>

      <div className="mt-6 flex flex-col items-center gap-2 py-6 text-center">
        <div className="bg-muted text-muted-foreground flex size-9 items-center justify-center rounded-full">
          <Lock className="size-4" />
        </div>
        <p className="text-sm font-medium">{t("detail.qc.lockedTitle")}</p>
        <p className="text-muted-foreground max-w-sm text-xs">
          {t("detail.qc.lockedHint")}
        </p>
      </div>
    </Card>
  )
}

function DisabledTab({
  icon,
  children,
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <span className="text-muted-foreground inline-flex cursor-not-allowed items-center gap-1.5 py-1 text-sm">
      {icon}
      {children}
    </span>
  )
}
